import { Component, computed, input, output, signal } from '@angular/core';
import type { ChatDuty } from '../../core/chat-api.service';

/** "Rest of the day" is ten hours: the API takes a minute count, and a special
 *  word would need a second code path that could disagree with the first about
 *  when the break ends. */
const DURATIONS: { minutes: number; label: string }[] = [
  { minutes: 15, label: '15 min' },
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
  { minutes: 600, label: 'Rest of the day' },
];

/**
 * Take a break, or end one. Pure input/output: no service, no HttpClient, no
 * Supabase. The parent turns `start` into POST /api/v2/chat/break and `end`
 * into **DELETE** /api/v2/chat/break — ending a break is its own route, never a
 * zero-minute start, which the API answers 400.
 *
 * Three rules this component exists to keep:
 *
 *  - **It is never hidden or disabled ahead of time.** GET /duty can be seconds
 *    stale, and a dead button with no explanation is worse than a clear
 *    refusal. When `onDutyCount` is 1 it carries a quiet warning; the refusal
 *    itself comes from the API, in the API's own words.
 *  - **Whether this person is on a break is the API's decision.** `meOnBreak`
 *    is computed server-side from `break_until > now`, and an expired break
 *    comes back as `meOnBreak: false, breakUntil: null`. Nothing here compares
 *    a timestamp to decide it. (Somebody *else's* break is the opposite case
 *    and is read from `PresenceEntry.breakUntil` in `chat-staff.component.ts`.)
 *  - **The countdown is only the remainder of a server-chosen instant.** No
 *    business-hours rule and no time zone enters into it; both sides of the
 *    subtraction are absolute instants.
 *
 * There is no reason to render: `ChatDutyDto` does not carry one. The API
 * stores the break reason and never returns it.
 *
 * Every class is prefixed `afs-chat-` and lives in this component's own inline
 * styles, so NobleUI's demo `.chat-*` set cannot reach it.
 */
@Component({
  selector: 'app-chat-break',
  standalone: true,
  styles: [`
    .afs-chat-break {
      display: flex; flex-wrap: wrap; align-items: center; gap: .35rem;
      font-size: .75rem;
    }
    .afs-chat-break__picker {
      display: flex; flex-wrap: wrap; gap: .3rem; width: 100%; padding-top: .3rem;
    }
    .afs-chat-break__reason {
      flex: 1; min-width: 8rem; border: 1px solid rgba(0,0,0,.2); border-radius: 4px;
      padding: .15rem .35rem; font: inherit; font-size: .75rem;
    }
    .afs-chat-break__warning, .afs-chat-break__refusal {
      width: 100%; margin: 0; font-size: .7rem;
    }
    .afs-chat-break__warning { opacity: .8; }
    .afs-chat-break__refusal { color: #b51f22; }
    /* Touch target: the field team drives this on an Android tablet. */
    @media (pointer: coarse) {
      .afs-chat-break__open, .afs-chat-break__end, .afs-chat-break__duration { min-height: 36px; }
      .afs-chat-break__reason { font-size: 1rem; min-height: 36px; }
    }
  `],
  template: `
    <div class="afs-chat-break">
      @if (duty(); as d) {
        @if (d.meOnBreak) {
          <span class="afs-chat-break__countdown">On break · back in {{ remainingMinutes() }} min</span>
          <button
            class="afs-chat-break__end btn btn-sm btn-outline-secondary"
            type="button"
            (click)="end.emit()"
          >End break now</button>
        } @else {
          <!-- Never disabled ahead of time: GET /duty can be seconds stale, and
               a dead button with no explanation is worse than a clear refusal. -->
          <button
            class="afs-chat-break__open btn btn-sm btn-outline-secondary"
            type="button"
            [attr.aria-expanded]="picking()"
            (click)="picking.set(!picking())"
          >Take a break</button>

          @if (d.onDutyCount === 1) {
            <p class="afs-chat-break__warning">You are the only one on duty right now.</p>
          }

          @if (picking()) {
            <div class="afs-chat-break__picker">
              <input
                class="afs-chat-break__reason"
                type="text"
                placeholder="Reason (optional)"
                aria-label="Reason (optional)"
                [value]="reason()"
                (input)="reason.set($any($event.target).value)"
              />
              @for (option of durations; track option.minutes) {
                <button
                  class="afs-chat-break__duration btn btn-sm btn-outline-secondary"
                  type="button"
                  (click)="choose(option.minutes)"
                >{{ option.label }}</button>
              }
            </div>
          }
        }

        @if (refusal(); as message) {
          <p class="afs-chat-break__refusal" role="alert">{{ message }}</p>
        }
      }
    </div>
  `,
})
export class ChatBreakComponent {
  readonly duty = input.required<ChatDuty | null>();
  /** The API's own wording for a refused break. Never paraphrased. */
  readonly refusal = input<string | null>(null);

  readonly start = output<{ minutes: number; reason: string | null }>();
  readonly end = output<void>();

  readonly durations = DURATIONS;
  readonly picking = signal(false);
  readonly reason = signal('');

  /**
   * How much of the break is left, in whole minutes. Clamped at zero: duty is
   * polled, so between the instant a break expires and the next GET /duty the
   * browser holds `meOnBreak: true` with a `break_until` that has already
   * passed, and "back in -3 min" is what that renders without the clamp.
   */
  readonly remainingMinutes = computed(() => {
    const until = this.duty()?.breakUntil;
    // Type narrowing only: the API never returns meOnBreak without an instant.
    if (!until) return 0;
    return Math.max(0, Math.round((Date.parse(until) - Date.now()) / 60_000));
  });

  choose(minutes: number): void {
    const typed = this.reason().trim();
    this.picking.set(false);
    this.reason.set('');
    this.start.emit({ minutes, reason: typed.length === 0 ? null : typed });
  }
}
